import React, { useState, useEffect } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import axios from "axios";
import { getDateFormat } from "@utils/datePickerUtils";
import {
  Dialog,
  Button,
  Anchor,
  TextInput,
  Image,
  Dropdown,
} from "hesus-ui-kit";
import ClipLoader from "react-spinners/ClipLoader";
import PulseLoader from "react-spinners/PulseLoader";
import { useGetGeneralParameters } from "@hooks/useGeneralParams";
import Header from "../Header/Header";
import DocumentDragDrop from "@components/DocumentDragDrop/DocumentDragDrop";
import { handleApiError } from "@utils/errorHandler";
import {
  renderPriceWithCurrency,
  addSpace,
  updateUnitTranslation,
} from "@utils/utils";
import {
  labelStyles,
  inputStyles,
  labelStylesError,
  inputStylesError,
  noLabelStyles,
  dropdownStyles,
  dropdownStylesError,
} from "@utils/extra-style";
import UserInfo from "@utils/UserInfo";

import {
  HeaderWrapper,
  Title,
  ButtonWrapper,
  FormBody,
  FlexRow,
  SectionContainer,
  GridRow,
  Labeltext,
  RowHeader,
  PlanningOuter,
  GridSubItem,
  GridItem,
  InvoiceOuter,
} from "./AddEditOtherSummary.styled";
import "./AddEditOtherSummary.scss";
import closeIcon from "@icons/ic-close-white.svg";
import enableIcon from "@icons/ic-toggle-green.svg";
import disableIcon from "@icons/ic-disable.svg";
import enableInvoiceReadyIcon from "@icons/ic-toggle-blue-green.svg";

const AddEditOtherSummary = (props) => {
  const {
    t,
    id,
    clientOrderItemId,
    siteId,
    isEditMode,
    onClose,
    updateRefeshStatus,
  } = props;
  const [isLoading, setIsLoading] = useState(true);
  const [isEntityCreating, setIsEntityCreating] = useState(false);
  const [createEntityAPIError, setCreateEntityAPIError] = useState("");
  const [summaryData, setSummaryData] = useState([]);
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const generalParams = useGetGeneralParameters();
  const { unitList } = generalParams;

  const userDetails = UserInfo.getUser();

  const [formData, setFormData] = useState({
    quantity: {
      required: true,
      value: summaryData?.summary?.quantity ?? "",
      error: "",
    },
    unit: {
      required: true,
      value: summaryData?.service?.sellingPriceUnit ?? "",
      error: "",
    },
    isReadyForInvoice: {
      required: true,
      value: summaryData?.summary?.isReadyForInvoicing ?? false,
      error: "",
    },
    isInvoiced: {
      required: true,
      value: summaryData?.summary?.isInvoiced ?? false,
      error: "",
    },
    isSummaryInvoiced: {
      required: true,
      value: summaryData?.summary?.isInvoiced ?? false,
      error: "",
    },
    documents: [],
  });

  useEffect(() => {
    if (summaryData?.clientOrderDate) {
      const formUpdatedInfo = {
        quantity: {
          required: true,
          value: summaryData?.summary?.quantity ?? "",
          error: "",
        },
        unit: {
          required: true,
          value: isEditMode
            ? summaryData?.summary?.unit
            : summaryData?.service?.sellingPriceUnit ?? "",
          error: "",
        },
        isReadyForInvoice: {
          required: true,
          value: summaryData?.summary?.isReadyForInvoicing ?? false,
          error: "",
        },
        isInvoiced: {
          required: true,
          value: summaryData?.summary?.isInvoiced ?? false,
          error: "",
        },
        isSummaryInvoiced: {
          required: true,
          value: summaryData?.summary?.isInvoiced ?? false,
          error: "",
        },
        documents: [],
      };
      setFormData(formUpdatedInfo);
    }
  }, [summaryData]);

  const fetchSummaryData = () => {
    setIsLoading(true);
    axios
      .get(`/otherServicesSummaries/${clientOrderItemId}`)
      .then((response) => {
        if (response.data?.[0]) {
          setSummaryData(response.data?.[0]);
          setIsLoading(false);
        }
      })
      .catch((error) => handleApiError(error));
  };

  useEffect(() => {
    fetchSummaryData();
  }, []);

  const onQuantityChange = (e) => {
    setFormData({
      ...formData,
      quantity: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const onUnitChange = (e) => {
    setFormData({
      ...formData,
      unit: {
        value: e.value,
        error: "",
        required: true,
      },
    });
  };

  const onInvoiceChange = () => {
    setFormData({
      ...formData,
      isReadyForInvoice: {
        value: !formData.isReadyForInvoice.value,
        error: "",
        required: true,
      },
    });
  };

  const onDocumentUpload = (documentArray) => {
    setSelectedFiles([...documentArray]);
    // setFormData({
    //   ...formData,
    //   documents: [...documentArray],
    // });
  };
  useEffect(() => {
    setFormData({
      ...formData,
      documents: [...selectedFiles],
    });
  }, [selectedFiles]);

  // useEffect(() => {
  //   if (selectedProduct && isEditMode) {
  //     setExistingDocuments(selectedProduct.documents);
  //   }
  // }, [summaryData?.documents, isEditMode]);

  const validateForm = (data) => {
    let isFormValid = true;
    if (!isEditMode) {
      if (!data.quantity.value && data.quantity.value !== 0) {
        isFormValid = false;
        data.quantity.error = t("FIELD_REQUIRED");
      }
      if (!data.unit.value) {
        isFormValid = false;
        data.unit.error = t("FIELD_REQUIRED");
      }
    }
    if (isEditMode) {
      if (
        data.quantity.value !== "" &&
        data.quantity.value !== 0 &&
        !data.unit.value
      ) {
        isFormValid = false;
        data.unit.error = t("FIELD_REQUIRED");
      }
    }
    if (!isFormValid) {
      setFormData({ ...data });
    }
    return isFormValid;
  };

  const getFormData = () => {
    const dataModified = new FormData();
    // const data = [];
    const data = {
      clientOrderItemId: summaryData?.clientOrderItemId,
      clientOrderId: summaryData?.clientOrderId,
      clientOrderDate: summaryData.clientOrderDate,
      site: summaryData.site,
      service: summaryData.service,
      summary: {
        id: isEditMode ? summaryData?.summary?.id : null,
        clientOrderItemId: summaryData?.clientOrderItemId,
        quantity: formData.quantity.value,
        unit: formData.unit.value,
        isReadyForInvoicing: formData.isReadyForInvoice.value,
        isInvoiced: formData.isReadyForInvoice.value,
        admin: { id: userDetails.id },
      },
    };

    dataModified.append("summary", JSON.stringify(data));

    if (formData.documents.length > 0) {
      // for (let i = 0; i < formData.documents.length; i++) {
      //   dataModified.append(`documents[${i}][type]`, 8);
      //   dataModified.append(
      //     `documents[${i}][filename]`,
      //     formData.documents[i].name
      //   );
      //   dataModified.append(`documents[${i}][file]`, formData.documents[i]);
      // }
      for (let i = 0; i < formData.documents.length; i++) {
        dataModified.append(`documents[${i}][document][type]`, 8);
        dataModified.append(
          `documents[${i}][document][filename]`,
          formData.documents[i].name
        );
        dataModified.append(
          `documents[${i}][document][file]`,
          formData.documents[i]
        );
      }
    }
    if (existingDocuments.length > 0) {
      let i = formData.documents.length > 0 ? formData.documents.length : 0;
      // existingDocuments.forEach((document) => {
      //   dataModified.append(`documents[${i}][filename]`, document.fileName);
      //   dataModified.append(`documents[${i}][id]`, document.id);
      //   dataModified.append(`documents[${i}][file]`, document.fileName);
      //   dataModified.append(`documents[${i}][type]`, 8);
      //   i += 1;
      // });
      existingDocuments.forEach((document) => {
        dataModified.append(
          `documents[${i}][document][filename]`,
          document.fileName
        );
        dataModified.append(`documents[${i}][document][id]`, document.id);
        dataModified.append(
          `documents[${i}][document][file]`,
          document.fileName
        );
        dataModified.append(`documents[${i}][document][type]`, 8);
        i += 1;
      });
    }
    return dataModified;
  };

  const onSubmit = () => {
    const isFormValid = validateForm(formData);
    if (isFormValid) {
      const data = getFormData(formData);
      setIsEntityCreating(true);
      setCreateEntityAPIError("");
      if (!isEditMode) {
        axios
          .post(
            `/otherServicesSummaries/${summaryData?.clientOrderItemId}/summaries`,
            data,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          )
          .then(() => {
            setIsEntityCreating(false);
            updateRefeshStatus();
            onClose();
          })
          .catch((error) => {
            setIsEntityCreating(false);
            setCreateEntityAPIError(error.response?.data?.message ?? "");
          });
      }
      // Edit
      if (isEditMode) {
        axios
          .post(
            `/otherServicesSummaries/${summaryData?.clientOrderItemId}/summaries/${summaryData?.summary?.id}`,
            data,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          )
          .then(() => {
            setIsEntityCreating(false);
            updateRefeshStatus();
            onClose();
          })
          .catch((error) => {
            setIsEntityCreating(false);
            setCreateEntityAPIError(error.response?.data?.message ?? "");
          });
      }
    }
  };

  useEffect(() => {
    if (summaryData && isEditMode) {
      setExistingDocuments(summaryData.documents);
    }
  }, [summaryData, isEditMode]);
  
  return (
    <Dialog width="65rem">
      <div className="add-edit-other-summary">
        <HeaderWrapper>
          <Title>
            {t("SUMMARIES_OTHER_SERVICES")}-{" "}
            {getDateFormat(summaryData?.clientOrderDate, "YYYY-MM-DD")}
          </Title>
          <Button
            onClick={onClose}
            styles={{
              backgroundColor: "transparent",
              border: "none",
              height: "auto",
              padding: 0,
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            <Image
              src={closeIcon}
              alt="close Product price popup"
              styles={{ width: "auto" }}
            />
          </Button>
        </HeaderWrapper>
        <FormBody>
          <FlexRow>
            <SectionContainer>
              <div>
                {isLoading && (
                  <div className="suggestions-list-loading">
                    <PulseLoader color="#1bace4" size={15} />
                  </div>
                )}
                {!isLoading && (
                  <>
                    <GridRow column="1fr auto">
                      <Labeltext>
                        <Anchor
                          textDecoration="underline"
                          url={`/site/view/${siteId}/general-info`}
                          className="add-edit-other-summary__product-link"
                          target="_blank"
                        >
                          #{siteId}
                        </Anchor>{" "}
                        - {summaryData.site.siteName}
                      </Labeltext>
                      <InvoiceOuter>
                        <Image
                          src={
                            formData?.isInvoiced?.value
                              ? enableIcon
                              : formData?.isReadyForInvoice?.value
                              ? enableInvoiceReadyIcon
                              : disableIcon
                          }
                          alt="readyforinvoice"
                          styles={{ width: "auto", marginBottom: "5px" }}
                          onClick={onInvoiceChange}
                        />
                        <Labeltext
                          margin="0 0px 0 7px"
                          color={
                            formData?.isInvoiced?.value
                              ? "#32A728"
                              : formData?.isReadyForInvoice?.value
                              ? "#008395"
                              : "#767676"
                          }
                        >
                          {t("SUMMARY_TOGGLE_READY_INVOICING")}
                        </Labeltext>
                      </InvoiceOuter>
                    </GridRow>

                    <RowHeader>
                      <GridRow
                        column="75% 25%"
                        marginTop="0px"
                        marginBottom="0px"
                        gridGap="0rem"
                      >
                        <Header
                          title="CLIENT_ORDER_SERVICE"
                          align="left"
                          borderRadius="left"
                        />
                        <Header
                          title="CLIENT_ORDER_PLANNED"
                          align="center"
                          borderRadius="right"
                        />
                      </GridRow>
                    </RowHeader>
                    <PlanningOuter marginBottom="30px">
                      <GridRow
                        column="75% 25%"
                        marginBottom="0px"
                        gridGap="0rem"
                        marginTop="0px"
                        borderBottom="2px solid rgba(10, 73, 134, 0.3)"
                      >
                        <GridItem
                          borderLeft="2px solid rgba(10, 73, 134, 0.3)"
                          borderRight="2px solid rgba(10, 73, 134, 0.3)"
                          alignContent="center"
                          padding="10px"
                        >
                          <GridRow
                            column="70% 30%"
                            marginBottom="0px"
                            marginTop="0px"
                            gridGap="0rem"
                          >
                            <GridSubItem
                              color="#0A4986"
                              fontWeight="700"
                              fontSize="16px"
                            >
                              {summaryData?.service?.hesusService?.name}
                            </GridSubItem>
                            <GridSubItem
                              fontWeight="400"
                              fontSize="16px"
                              textAlign="right"
                              color="#474747"
                              alignContent="center"
                            >
                              {renderPriceWithCurrency(
                                addSpace(summaryData?.service?.sellingPrice),
                                t(summaryData?.service?.sellingPriceUnit),
                                summaryData.service?.quoteCurrency?.symbol,
                                summaryData.service?.quoteCurrency?.position
                              )}
                            </GridSubItem>
                          </GridRow>
                        </GridItem>
                        <GridSubItem
                          fontWeight="700"
                          fontSize="16px"
                          textAlign="center"
                          color="#474747"
                          alignContent="center"
                          borderRight="2px solid rgba(10, 73, 134, 0.3)"
                        >
                          {summaryData?.orderQuantity}{" "}
                          {t(summaryData?.orderUnit)}
                        </GridSubItem>
                      </GridRow>
                    </PlanningOuter>
                    <GridRow column="1fr">
                      <Labeltext color="#15aee5" fontSize="1.25rem">
                        {t("OTHER_SERVICES_SUMMARY_TITLE")}
                      </Labeltext>
                    </GridRow>

                    <RowHeader>
                      <GridRow
                        column="60% 40%"
                        marginTop="0px"
                        marginBottom="0px"
                        gridGap="0rem"
                      >
                        <Header
                          title="SUMMARIES_FORM_SERVICE"
                          align="left"
                          borderRadius="left"
                          color="#008395"
                        />
                        <Header
                          title="SUMMARIES_FORM_REALISED"
                          align="center"
                          borderRadius="right"
                          color="#008395"
                        />
                      </GridRow>
                    </RowHeader>
                    <PlanningOuter marginBottom="30px">
                      <GridRow
                        column="60% 40%"
                        marginBottom="0px"
                        gridGap="0rem"
                        marginTop="0px"
                        borderBottom="2px solid rgba(10, 73, 134, 0.3)"
                      >
                        <GridItem
                          borderLeft="2px solid rgba(10, 73, 134, 0.3)"
                          borderRight="2px solid rgba(10, 73, 134, 0.3)"
                          alignContent="center"
                        >
                          <GridSubItem
                            color="#0A4986"
                            fontWeight="700"
                            fontSize="16px"
                          >
                            {summaryData?.service?.hesusService?.name}
                          </GridSubItem>
                        </GridItem>
                        <GridSubItem
                          fontWeight="700"
                          fontSize="16px"
                          textAlign="center"
                          color="#474747"
                          alignContent="center"
                          borderRight="2px solid rgba(10, 73, 134, 0.3)"
                          padding="10px 30px"
                        >
                          {!!formData.isSummaryInvoiced.value && (
                            <GridRow column="1fr">
                              {formData.quantity.value +
                                " " +
                                t(formData.unit.value)}
                            </GridRow>
                          )}
                          {!formData.isSummaryInvoiced.value && (
                            <GridRow
                              column="1fr 1fr"
                              marginTop="0px"
                              marginBottom="0px"
                            >
                              <TextInput
                                id="summary-quantity"
                                type="text"
                                isFloat
                                value={String(formData.quantity.value)}
                                onChange={(e) => onQuantityChange(e)}
                                errorStyles={{ "font-style": "italic" }}
                                labelStyles={
                                  formData.quantity.error !== ""
                                    ? labelStylesError
                                    : labelStyles
                                }
                                inputStyles={
                                  formData.quantity.error !== ""
                                    ? inputStylesError
                                    : inputStyles
                                }
                                error={formData.quantity.error}
                                placeholder=""
                              />
                              <Dropdown
                                id="other-serive-unit"
                                name="other-serive-unit"
                                value={formData.unit.value}
                                onChange={onUnitChange}
                                defaultOption={t("UNIT")}
                                options={updateUnitTranslation(unitList, t)}
                                required={false}
                                labelStyles={noLabelStyles}
                                containerStyles={{ lineHeight: "1.90rem" }}
                                inputStyles={
                                  formData.unit.error !== ""
                                    ? dropdownStylesError
                                    : dropdownStyles
                                }
                                error={formData.unit.error}
                                disabled
                              />
                            </GridRow>
                          )}
                        </GridSubItem>
                      </GridRow>
                    </PlanningOuter>

                    <GridRow>
                      <DocumentDragDrop
                        isEditMode={isEditMode}
                        onDocumentUpload={onDocumentUpload}
                        documentList={formData?.documents}
                        selectedProduct={summaryData}
                        existingDocuments={existingDocuments}
                        setExistingDocuments={setExistingDocuments}
                        description={t("SUMMARY_FORM_OTHER_DOCUMENT_LABEL")}
                      />
                    </GridRow>
                  </>
                )}
              </div>
            </SectionContainer>
          </FlexRow>
        </FormBody>

        <ButtonWrapper>
          {createEntityAPIError && <div>{createEntityAPIError}</div>}
          <Button
            styles={{
              color: "#333",
              background: "#fff",
              marginRight: "0.32rem",
              border: "1px solid #ccc",
              "&:hover": {
                backgroundColor: "#ccc",
              },
            }}
            onClick={onClose}
          >
            {t("CANCEL")}
          </Button>
          <Button
            styles={{
              color: "white",
              background: "#1BACE4",
              border: "1px solid #1BACE4",
              "&:hover": {
                backgroundColor: "#1589b6",
              },
            }}
            onClick={onSubmit}
          >
            <>
              {isEntityCreating && <ClipLoader size={15} color="#fff" />}
              {!isEntityCreating && t("VALIDATE")}
            </>
          </Button>
        </ButtonWrapper>
      </div>
    </Dialog>
  );
};

AddEditOtherSummary.propTypes = {
  t: PropTypes.func.isRequired,
  id: PropTypes.number,
  isEditMode: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  // updateDisputeRefeshStatus: PropTypes.func.isRequired,
  siteId: PropTypes.number.isRequired,
  planningId: PropTypes.number,
};

AddEditOtherSummary.defaultProps = {
  id: null,
  isEditMode: false,
};

export default withTranslation()(AddEditOtherSummary);
